import axios, { AxiosError, AxiosResponse } from 'axios';
import { STATUS_CODE } from '../constants/define/http-status-code';
import { MESSAGE_ERROR } from '../constants/message';
import { TokenRepository } from '../repository/web-storage/token';
import { BadRequestError } from './error';

export async function getData<T, R>(
  requestUrl: string,
  requestParams?: T,
  token?: string,
  clientId?: string,
  requestHeader?: T,
): Promise<AxiosResponse<R>> {
  try {
    let idToken = token;
    if (!token) {
      idToken = (await TokenRepository.getToken())?.idToken;
    }
    const headers = {
      Authorization: idToken,
      clientId: clientId || process.env.REACT_APP_ADMIN_PORTAL_CLIENT_ID,
      ...requestHeader,
    };
    return await axios.get<R>(requestUrl, { params: requestParams, headers });
  } catch (error) {
    return errorHandling(error);
  }
}

export async function postData<T, R>(
  requestUrl: string,
  requestBody: T,
  token?: string,
  clientId?: string,
): Promise<AxiosResponse<R>> {
  try {
    let idToken = token;
    if (!token) {
      idToken = (await TokenRepository.getToken())?.idToken;
    }
    const headers = {
      Authorization: idToken,
      clientId: clientId || process.env.REACT_APP_ADMIN_PORTAL_CLIENT_ID,
    };
    return await axios.post<R>(requestUrl, requestBody, { headers });
  } catch (error) {
    return errorHandling(error);
  }
}

export async function putData<T, R>(
  requestUrl: string,
  requestBody: T,
  token?: string,
  clientId?: string,
): Promise<AxiosResponse<R>> {
  try {
    let idToken = token;
    if (!token) {
      idToken = (await TokenRepository.getToken())?.idToken;
    }
    const headers = {
      Authorization: idToken,
      clientId: clientId || process.env.REACT_APP_ADMIN_PORTAL_CLIENT_ID,
    };
    return await axios.put<R>(requestUrl, requestBody, { headers });
  } catch (error) {
    return errorHandling(error);
  }
}

export async function patchData<T, R>(
  requestUrl: string,
  requestBody: T,
  token?: string,
  clientId?: string,
): Promise<AxiosResponse<R>> {
  try {
    let idToken = token;
    if (!token) {
      idToken = (await TokenRepository.getToken())?.idToken;
    }
    const headers = {
      Authorization: idToken,
      clientId: clientId || process.env.REACT_APP_ADMIN_PORTAL_CLIENT_ID,
    };
    return await axios.patch<R>(requestUrl, requestBody, { headers });
  } catch (error) {
    return errorHandling(error);
  }
}

export async function delData<R>(requestUrl: string, token?: string, clientId?: string): Promise<AxiosResponse<R>> {
  try {
    let idToken = token;
    if (!token) {
      idToken = (await TokenRepository.getToken())?.idToken;
    }
    const headers = {
      Authorization: idToken,
      clientId: clientId || process.env.REACT_APP_ADMIN_PORTAL_CLIENT_ID,
    };
    return await axios.delete<R>(requestUrl, { headers });
  } catch (error) {
    return errorHandling(error);
  }
}

const errorHandling = (err: any): any => {
  if (err && err.response) {
    const axiosError = err as AxiosError;
    switch (axiosError.response?.status) {
      case STATUS_CODE.BAD_REQUEST: {
        const data = axiosError.response?.data as BadRequestErrorType;
        throw new BadRequestError(
          axiosError.response?.status,
          data?.errorMessage || (data.message.length ? data.message : MESSAGE_ERROR.M02),
          data?.internalErrorCode,
        );
      }
      case STATUS_CODE.AUTHENTICATION_ERROR: {
        TokenRepository.remove();
        break;
      }
      case STATUS_CODE.PERMISSION_ERROR: {
        break;
      }
      default: {
        break;
      }
    }
  } else {
    // Network error or other errors without response (e.g., no internet connection)
    const axiosError = err as AxiosError;
    let errorMessage = 'An unexpected error occurred';

    // Check for specific axios error codes
    if (axiosError.code === 'ERR_NETWORK') {
      errorMessage = 'Network error. Please check your internet connection.';
    } else if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ERR_CANCELED') {
      errorMessage = 'Request timeout. Please try again.';
    } else if (axiosError.code === 'ERR_BAD_REQUEST') {
      errorMessage = 'Invalid request. Please check your input.';
    } else if (axiosError.message) {
      // Check if message contains network-related keywords
      const lowerMessage = axiosError.message.toLowerCase();
      if (lowerMessage.includes('network error') || lowerMessage.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (lowerMessage.includes('timeout')) {
        errorMessage = 'Request timeout. Please try again.';
      } else {
        // Use the original error message
        errorMessage = axiosError.message;
      }
    }

    throw new BadRequestError(STATUS_CODE.NETWORK_ERROR, errorMessage, axiosError.code || '');
  }
};
interface BadRequestErrorType {
  errorMessage: string | Object | any;
  internalErrorCode: string;
  path: string;
  timestamp: string;
  message: string[] | any;
}
